import React, {useEffect, useRef, useState} from 'react'
import GenericTable from '../../shared/generic-table'
import '../../../styles/control-center-styles.css'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import advanced from 'dayjs/plugin/advancedFormat'
import timezone from 'dayjs/plugin/timezone'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'
import {closeIcon} from '../../../assets'
import {toast} from 'react-toastify'
import {useAuthClient} from '../../../utils/use-auth-client'

dayjs.extend(timezone)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advanced)

const AppointmentInformationModal = ({children, open, onClose}) => {
  if (!open) return null

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <section className="appointment-info-modal">
        <div className="appointment-modal-header">
          <h2>Appointment Information</h2>
          <button onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="appointment-info-content">{children}</div>
      </section>
    </>
  )
}

const CancelConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  appointmentData,
}) => {
  if (!open) return null
  const formattedDate = formatDate(
    `${appointmentData.date.split('T')[0]} ${appointmentData.time}`,
    appointmentData.timezone,
  )

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <section className="cancel-confirmation-modal">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <h2 className="control-center-item-header" style={{margin: 0}}>
            Cancel Appointment
          </h2>
          <button onClick={onClose}>
            <img src={closeIcon} alt="" />
          </button>
        </div>
        <div className="cancel-confirmation-content">
          <p>Are you sure you want to cancel this appointment?</p>
          {appointmentData && (
            <div className="appointment-details">
              <p>
                <strong>Company:</strong> {appointmentData.company_name}
              </p>
              <p>
                <strong>Date:</strong> {formattedDate?.split(' ')[0]}
              </p>
              <p>
                <strong>Time:</strong> {formattedDate?.split(' ')[1]}{' '}
                {formattedDate?.split(' ')[2]}
              </p>
            </div>
          )}
          <div style={{display: 'flex', gap: '16px'}}>
            <button
              onClick={onClose}
              style={{position: 'static'}}
              className="confirm-modal-btn confirm"
            >
              Keep Appointment
            </button>
            <button
              onClick={onConfirm}
              style={{position: 'static'}}
              className="confirm-modal-btn"
            >
              Yes, Cancel Appointment
            </button>
          </div>
        </div>
      </section>
    </>
  )
}

const formatDate = (dateStr, timezone) => {
  return dayjs(dateStr).tz(timezone).format('MM/DD/YYYY HH:mmA z')
}

// Custom hook for debounced value
const useDebouncedValue = (value, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => clearTimeout(timer)
  }, [value, delay])

  return debouncedValue
}

const ScheduledAppointments = ({
  keapPartnerInfo,
  setDashboardLoading,
  dashboardLoading,
  title,
}) => {
  const [rows, setRows] = useState([])
  const [page, setPage] = useState(1)
  const [countPerPage] = useState(5)
  const [isLoading, setIsLoading] = useState(false)
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false)
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [modalInformation, setModalInformation] = useState(null)
  const [cancelModal, setCancelModal] = useState({
    open: false,
    appointmentData: null,
  })

  // Filter state consolidated
  const [filters, setFilters] = useState({
    clientFullName: '',
    startDate: '',
    endDate: '',
    status: '',
  })

  // Debounced filter values
  const debouncedClientName = useDebouncedValue(filters.clientFullName)
  const debouncedStartDate = useDebouncedValue(filters.startDate)
  const debouncedEndDate = useDebouncedValue(filters.endDate)
  const debouncedStatus = useDebouncedValue(filters.status)

  const dateRangeRef = useRef(null)
  const client = useAuthClient()
  const handleCancelConfirm = () => {
    if (cancelModal.appointmentData) {
      cancelAppointment(cancelModal.appointmentData.appt_id)
    }
    setCancelModal({open: false, appointmentData: null})
  }

  const handleCancelClose = () => {
    setCancelModal({open: false, appointmentData: null})
  }

  const cancelAppointment = async appointmentId => {
    setIsLoading(true)
    try {
      const appointmentObj = {
        schedule_status: 'Cancelled',
        appt_id: appointmentId,
      }
      const result = await client(
        'ordercapture/services/updateScheduledAppointmentStatus',
        {
          data: appointmentObj,
          method: 'PUT',
        },
      )
      if (result.success) {
        toast.success('Appointment Cancelled')
        const updatedRows = rows.map(row => {
          if (row.appt_id === appointmentId) {
            return {...row, appt_status: 'Cancelled'}
          }
          return row
        })

        setRows(updatedRows)
      } else {
        toast.error('Error in Appointment cancel')
      }
    } catch (e) {
      toast.warn('There was a problem with the cancellation')
    }
    setIsLoading(false)
  }

  // Filter update handlers
  const updateFilter = (key, value) => {
    setFilters(prev => {
      const updated = {...prev, [key]: value}

      // If start date is greater than end date, reset end date
      if (key === 'startDate' && prev.endDate && value > prev.endDate) {
        updated.endDate = ''
      }

      return updated
    })
  }

  const clearAllFilters = () => {
    setFilters({
      clientFullName: '',
      startDate: '',
      endDate: '',
      status: '',
    })
    setIsDateRangeOpen(false)
    setPage(1)
  }

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        isDateRangeOpen &&
        dateRangeRef.current &&
        !dateRangeRef.current.contains(event.target)
      ) {
        setIsDateRangeOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isDateRangeOpen])

  const columns = [
    {
      key: 'clientFullName',
      label: 'Client Full Name',
      accessor: row => `${row.contact_first_name} ${row.contact_last_name}`,
    },
    {key: 'company_name', label: 'Company'},
    {key: 'staff_id', label: 'Presenter'},
    {
      key: 'datetime',
      label: 'Date & Time',
      accessor: row => ({
        date: row.date,
        time: row.time,
        timezone: row.timezone,
      }),
      render: value => {
        const {date, time, timezone} = value
        return (
          <p className="formmated-date">
            {formatDate(`${date.split('T')[0]} ${time}`, timezone)}
          </p>
        )
      },
    },
    {
      key: 'actions',
      render: (value, row) => {
        const handleCancel = () => {
          setCancelModal({open: true, appointmentData: row})
        }
        if (row.zoomurl) {
          return (
            <div
              className="flex"
              style={{
                gap: '16px',
                justifyContent: 'flex-end',
              }}
            >
              <button
                className="table-button"
                onClick={() => setModalInformation(row)}
              >
                Details
              </button>
              <button
                className="table-button"
                onClick={() =>
                  addToCalendar(
                    dayjs(`${row.date.split('T')[0]} ${row.time}`).format(),
                    row.appt_id,
                  )
                }
              >
                Add To Calendar
              </button>
              <button
                className={`table-button ${
                  row.appt_status === 'Cancelled' ? 'canceled' : ''
                }`}
                onClick={handleCancel}
              >
                {row.appt_status === 'Cancelled' ? 'Cancelled' : 'Cancel'}
              </button>
            </div>
          )
        }
        return <span style={{color: '#666'}}>No action</span>
      },
    },
  ]

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [
    debouncedClientName,
    debouncedStartDate,
    debouncedEndDate,
    debouncedStatus,
  ])

  useEffect(() => {
    const fetchAppointments = async () => {
      setIsLoading(true)
      setDashboardLoading(true)

      // Build URL with query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        countPerPage: countPerPage.toString(),
      })

      if (debouncedClientName)
        params.append('clientFullName', debouncedClientName)
      if (debouncedStartDate) params.append('startDate', debouncedStartDate)
      if (debouncedEndDate) params.append('endDate', debouncedEndDate)
      if (debouncedStatus) params.append('status', debouncedStatus)

      const url = `keappartner/users/${keapPartnerInfo.keapPartnerId}/appointments?${params}`

      try {
        const data = await client(url)

        const appointments = Array.isArray(data?.Appointments)
          ? data.Appointments
          : []
        setRows(appointments)
        if (data?.pagination) {
          setPagination({
            page: data.pagination.page,
            totalPages: data.pagination.totalPages,
            total: data.pagination.total,
            hasNext: data.pagination.hasNext,
            hasPrev: data.pagination.hasPrev,
          })
        }
      } catch (e) {
        toast.error('Error fetching appointments')
        setRows([])
      } finally {
        setIsLoading(false)
        setDashboardLoading(false)
      }
    }

    fetchAppointments()
  }, [
    page,
    countPerPage,
    debouncedClientName,
    debouncedStartDate,
    debouncedEndDate,
    debouncedStatus,
    setDashboardLoading,
    client,
    keapPartnerInfo.keapPartnerId,
  ])

  const formatZoomUrl = zoomUrl => {
    if (!zoomUrl) return ''

    if (zoomUrl?.indexOf('?pwd') === -1 && zoomUrl?.indexOf('pwd') !== -1) {
      let pos = zoomUrl?.indexOf('pwd')
      return zoomUrl?.slice(0, pos) + '?pwd' + zoomUrl?.slice(pos + 3)
    } else {
      return zoomUrl
    }
  }

  const addToCalendar = async (dateUTC, appointmentId) => {
    const responseList = await client(`appointments/${appointmentId}`)
    const response =
      (responseList && responseList.length > 0 && responseList[0]) || {}

    const description = `Demo for ${response.cust_first_name} ${
      response.cust_last_name
    } at
    ${response.cust_company}. ${response.cust_first_name} can be contacted at ${
      response.cust_phone
    }
    or ${
      response.cust_email
    }. Please use the following link to attend: ${formatZoomUrl(
      response.demo_url,
    )}`

    const date = dayjs(dateUTC)
    const endDate = date.add(30, 'minute')

    let url = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//hacksw/handcal//NONSGML v1.0//EN',
      'BEGIN:VEVENT',
      'UID:' + date.format('YYYYMMDDTHHmmss'),
      'DTSTAMP:' + dayjs().format('YYYYMMDDTHHmmss'),
      'DTSTART:' + date.format('YYYYMMDDTHHmmss'),
      'DTEND:' + endDate.format('YYYYMMDDTHHmmss'),
      'SUMMARY:Demo',
      'LOCATION:See Invite Details for Zoom Link',
      'DESCRIPTION:' + description,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\n')

    let blob = new Blob([url], {type: 'text/calendar;charset=utf-8'})

    if (/msie\s|trident\/|edge\//i.test(window.navigator.userAgent)) {
      // Open/Save link in IE and Edge
      console.log('IE Edge detected, ', window.navigator.userAgent)
      window.navigator.msSaveBlob(blob, 'thryv-event.ics')
    } else {
      // Open/Save link in Modern Browsers
      const a = document.createElement('a')
      document.body.appendChild(a)
      a.style = 'display: none'
      const url2 = window.URL.createObjectURL(blob)
      a.href = url2
      a.download = 'thryv-event.ics'
      a.click()
      window.URL.revokeObjectURL(url2)
    }
  }

  const handleDateRangeChange = value => {
    if (Array.isArray(value)) {
      const [start, end] = value
      const startStr = start ? dayjs(start).format('YYYY-MM-DD') : ''
      const endStr = end ? dayjs(end).format('YYYY-MM-DD') : ''

      updateFilter('startDate', startStr)
      updateFilter('endDate', endStr)

      if (start && end) {
        setIsDateRangeOpen(false)
      }
    }
  }

  const dateRangeLabel = filters.startDate
    ? filters.endDate
      ? `${dayjs(filters.startDate).format('MM/DD/YYYY')} - ${dayjs(
          filters.endDate,
        ).format('MM/DD/YYYY')}`
      : `${dayjs(filters.startDate).format('MM/DD/YYYY')} - Select end date`
    : ''

  const getCalendarValue = () => {
    if (!filters.startDate && !filters.endDate) return null

    const toLocalDate = dateStr => {
      if (!dateStr) return null
      const [year, month, day] = dateStr.split('-').map(Number)
      return new Date(year, month - 1, day)
    }

    return [toLocalDate(filters.startDate), toLocalDate(filters.endDate)]
  }

  return (
    <>
      <div style={{padding: 16}}>
        <h3
          className="control-center-item-header"
          style={{marginBottom: '8px'}}
        >
          {title}
        </h3>
        {rows.length > 0 && (
          <h4
            className="control-center-display-text"
            style={{marginBottom: '16px'}}
          >
            Displaying {page * countPerPage - countPerPage + 1}-
            {page * countPerPage} out of {pagination.total}
          </h4>
        )}
        <div
          style={{
            padding: '16px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            display: 'flex',
            gap: '24px',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            border: '1px solid #e9e9e9',
          }}
        >
          <div style={{flex: '1 1 auto', minWidth: '250px'}}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                marginBottom: '6px',
                fontWeight: '600',
                textTransform: 'uppercase',
                color: '#666',
                letterSpacing: '0.5px',
              }}
            >
              Client Name
            </label>
            <input
              type="text"
              placeholder="Enter client name to filter"
              value={filters.clientFullName}
              onChange={e => updateFilter('clientFullName', e.target.value)}
              style={{
                padding: '10px 12px',
                borderRadius: '4px',
                border: '1px solid #d4d4d4',
                fontSize: '14px',
                fontFamily: 'Open Sans, sans-serif',
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                color: '#231f20',
                transition: 'border-color 0.2s',
              }}
              onFocus={e => (e.target.style.borderColor = '#fa6000')}
              onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
            />
          </div>
          <div style={{flex: '1 1 auto', minWidth: '260px'}} ref={dateRangeRef}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                marginBottom: '6px',
                fontWeight: '600',
                textTransform: 'uppercase',
                color: '#666',
                letterSpacing: '0.5px',
              }}
            >
              Date Range
            </label>
            <div style={{position: 'relative'}}>
              <input
                type="text"
                readOnly
                value={dateRangeLabel}
                placeholder="Select date range"
                onClick={() => setIsDateRangeOpen(prev => !prev)}
                style={{
                  padding: '10px 12px',
                  borderRadius: '4px',
                  border: '1px solid #d4d4d4',
                  fontSize: '14px',
                  fontFamily: 'Open Sans, sans-serif',
                  width: '100%',
                  boxSizing: 'border-box',
                  backgroundColor: '#ffffff',
                  color: '#231f20',
                  transition: 'border-color 0.2s',
                  cursor: 'pointer',
                }}
                onFocus={e => (e.target.style.borderColor = '#fa6000')}
                onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
              />
              {isDateRangeOpen && (
                <div
                  style={{
                    position: 'absolute',
                    zIndex: 20,
                    marginTop: '8px',
                    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.12)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  <Calendar
                    selectRange
                    onChange={handleDateRangeChange}
                    value={getCalendarValue()}
                  />
                </div>
              )}
            </div>
          </div>
          <div style={{flex: '1 1 auto', minWidth: '180px'}}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                marginBottom: '6px',
                fontWeight: '600',
                textTransform: 'uppercase',
                color: '#666',
                letterSpacing: '0.5px',
              }}
            >
              Status
            </label>
            <select
              value={filters.status}
              onChange={e => updateFilter('status', e.target.value)}
              style={{
                padding: '10px 12px',
                borderRadius: '4px',
                border: '1px solid #d4d4d4',
                fontSize: '14px',
                fontFamily: 'Open Sans, sans-serif',
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                color: '#231f20',
                transition: 'border-color 0.2s',
                cursor: 'pointer',
              }}
              onFocus={e => (e.target.style.borderColor = '#fa6000')}
              onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
            >
              <option value="">All Statuses</option>
              <option value="Scheduled">Scheduled</option>
              <option value="Expired">Expired</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
          <div style={{flex: '0 0 auto', minWidth: '120px'}}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                marginBottom: '6px',
                fontWeight: '600',
                textTransform: 'uppercase',
                color: 'transparent',
                letterSpacing: '0.5px',
              }}
            >
              &nbsp;
            </label>
            <button
              onClick={clearAllFilters}
              style={{
                padding: '10px 16px',
                borderRadius: '4px',
                border: '1px solid #d4d4d4',
                fontSize: '14px',
                fontFamily: 'Open Sans, sans-serif',
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                color: '#fa6000',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => {
                e.target.style.backgroundColor = '#fa6000'
                e.target.style.color = '#ffffff'
                e.target.style.borderColor = '#fa6000'
              }}
              onMouseLeave={e => {
                e.target.style.backgroundColor = '#ffffff'
                e.target.style.color = '#fa6000'
                e.target.style.borderColor = '#d4d4d4'
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>
      {rows.length > 0 && (
        <div style={{padding: 16}}>
          <GenericTable
            columns={columns}
            rows={rows}
            emptyMessage="No appointments found."
            isLoading={isLoading}
            loadingMessage="Loading appointments..."
            pagination={{
              page: pagination.page,
              totalPages: pagination.totalPages,
              hasPrev: pagination.hasPrev,
              hasNext: pagination.hasNext,
              onPageChange: setPage,
            }}
          />
          <AppointmentInformationModal
            open={Boolean(modalInformation)}
            onClose={() => setModalInformation(null)}
          >
            {modalInformation && (
              <div className="appointment-details-container">
                <div className="appointment-details-grid">
                  <div className="appointment-detail">
                    <span className="detail-label">Appointment Type</span>
                    <span className="detail-value">
                      {modalInformation.appointment_type || 'N/A'}
                    </span>
                  </div>
                  <div className="appointment-detail">
                    <span className="detail-label">Appointment ID</span>
                    <span className="detail-value">
                      {modalInformation.appt_id || 'N/A'}
                    </span>
                  </div>
                  <div className="appointment-detail">
                    <span className="detail-label">Status</span>
                    <span
                      className={`status-badge ${
                        modalInformation.appt_status?.toLowerCase() || ''
                      }`}
                    >
                      {modalInformation.appt_status || 'N/A'}
                    </span>
                  </div>
                  <div className="appointment-detail">
                    <span className="detail-label">Company</span>
                    <span className="detail-value">
                      {modalInformation.company_name || 'N/A'}
                    </span>
                  </div>
                  <div className="appointment-detail">
                    <span className="detail-label">Date & Time</span>
                    <span className="detail-value">
                      {modalInformation.date} at {modalInformation.time}{' '}
                      {modalInformation.timezone}
                    </span>
                  </div>
                  <div className="appointment-detail">
                    <span className="detail-label">Duration</span>
                    <span className="detail-value">
                      {modalInformation.duration || 'N/A'}
                    </span>
                  </div>
                  <div className="appointment-detail">
                    <span className="detail-label">Order ID</span>
                    <span className="detail-value">
                      {modalInformation.order_id || 'N/A'}
                    </span>
                  </div>
                  <div className="appointment-detail">
                    <span className="detail-label">Service ID</span>
                    <span className="detail-value">
                      {modalInformation.service_id || 'N/A'}
                    </span>
                  </div>
                  <div className="appointment-detail">
                    <span className="detail-label">Staff ID</span>
                    <span className="detail-value">
                      {modalInformation.staff_id || 'N/A'}
                    </span>
                  </div>
                  <div className="appointment-detail">
                    <span className="detail-label">Title</span>
                    <span className="detail-value">
                      {modalInformation.title || 'N/A'}
                    </span>
                  </div>
                  {modalInformation.zoomurl && (
                    <div className="appointment-detail full-width">
                      <span className="detail-label">Meeting Link</span>
                      <a
                        href={modalInformation.zoomurl}
                        target="_blank"
                        rel="noreferrer"
                        className="zoom-link"
                      >
                        Join Zoom Meeting
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </AppointmentInformationModal>

          <CancelConfirmationModal
            open={cancelModal.open}
            onClose={handleCancelClose}
            onConfirm={handleCancelConfirm}
            appointmentData={cancelModal.appointmentData}
          />
        </div>
      )}
      {!(rows.length > 0) && !dashboardLoading && (
        <div style={{padding: 16}}>
          <div
            style={{
              textAlign: 'center',
              paddingTop: '60px',
              paddingBottom: '60px',
            }}
          >
            <h4
              style={{
                color: '#808080',
                fontSize: '16px',
                fontWeight: '600',
              }}
            >
              No Scheduled Appointments available
            </h4>
            <p style={{color: '#999', fontSize: '14px', marginTop: '8px'}}>
              Try adjusting your filters to see more results
            </p>
          </div>
        </div>
      )}
    </>
  )
}

export default ScheduledAppointments
